// ============================================================================
// SalaryProgressionChart (F-756) — frise salaire/boni/avantages, SVG maison
// ============================================================================
// Portage FIDELE du PoC salary-progression-chart.tsx (427 L). Enveloppe
// FinanceYearChart (E2) : SVG inline, AUCUNE dependance de charting, rendu 1:1
// (viewBox = pixels) pour que le texte ne soit jamais etire.
//
// COULEURS (BL-12, parite FinanceYearChart) : le PoC prenait var(--gs-red) (sa
// variable de marque, ABSENTE de gest) -> on prend la MARQUE runtime
// rgb(var(--brand-500-rgb)) (branding par compagnie, ADR-059). Boni/avantages
// gardent --chart-cat-2/3 (definis dans src/index.css). Zero hex, zero taille
// litterale hors geometrie SVG (echelle interne du graphe).

import { useTranslation } from '../../i18n';
import type { SalaryTimeline } from '../../lib/salary/progression';

const VIEW_W = 960;
const VIEW_H = 320;
const PAD_L = 46;
const PAD_R = 40;
const PAD_T = 64;
const PAD_B = 32;

const SALARY_COLOR = 'rgb(var(--brand-500-rgb))';
const BONUS_COLOR = 'var(--chart-cat-2)';
const BENEFIT_COLOR = 'var(--chart-cat-3)';

const GRID_TICKS = 4;

const compactDollar = (v: number): string => `${Math.round(v / 1000)} k$`;
const truncate = (s: string, max: number): string => (s.length > max ? `${s.slice(0, max - 1)}…` : s);

function pctLabel(prev: number, cur: number): string | null {
  if (prev <= 0) return null;
  const pct = cur / prev - 1;
  if (Math.abs(pct) < 0.0005) return null;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${(pct * 100).toLocaleString('fr-CA', { maximumFractionDigits: 1 })} %`;
}

export function SalaryProgressionChart({
  timeline,
  bonusByYear,
  bonusProjByYear,
  avantages,
}: {
  timeline: SalaryTimeline;
  bonusByYear: Map<number, number>;
  bonusProjByYear?: Map<number, number>;
  avantages: { year: number; annual: number } | null;
}) {
  const { t } = useTranslation();
  const points = timeline.points;
  const valued = points.filter((p) => p.annual !== null);
  if (valued.length === 0) return null;

  const years = points.map((p) => p.year);
  const startYear = Math.min(...years);
  const endYear = Math.max(...years);
  const currentYear = points.find((p) => p.kind === 'current')?.year ?? endYear;

  const bonusPts = [...bonusByYear.entries()]
    .filter(([year, v]) => year >= startYear && year <= endYear && v > 0)
    .map(([year, value]) => ({ year, value }))
    .sort((a, b) => a.year - b.year);
  const bonusProjPts = [...(bonusProjByYear?.entries() ?? [])]
    .filter(([year, v]) => year >= startYear && year <= endYear && v > 0)
    .map(([year, value]) => ({ year, value }))
    .sort((a, b) => a.year - b.year);
  const benefitPt =
    avantages && avantages.annual > 0 && avantages.year >= startYear && avantages.year <= endYear ? avantages : null;

  const allValues = [
    ...valued.map((p) => p.annual as number),
    ...bonusPts.map((b) => b.value),
    ...bonusProjPts.map((b) => b.value),
    ...(benefitPt ? [benefitPt.annual] : []),
  ];
  const minV = Math.min(...allValues);
  const maxV = Math.max(...allValues);
  const span = maxV - minV || 1;
  const pad = span * 0.12;
  const domainMin = minV - pad;
  const domainMax = maxV + pad;

  const x0 = PAD_L;
  const x1 = VIEW_W - PAD_R;
  const y0 = PAD_T;
  const y1 = VIEW_H - PAD_B;

  const xFor = (year: number) =>
    endYear === startYear ? (x0 + x1) / 2 : x0 + ((year - startYear) / (endYear - startYear)) * (x1 - x0);
  const yFor = (v: number) => y1 - ((v - domainMin) / (domainMax - domainMin)) * (y1 - y0);

  const coord = (p: { year: number; annual: number | null }) => [xFor(p.year), yFor(p.annual as number)] as const;
  const polyline = (pts: typeof valued) => pts.map((p) => coord(p).map((n) => n.toFixed(1)).join(',')).join(' ');

  const pastPts = valued.filter((p) => p.year <= currentYear);
  const futurePts = valued.filter((p) => p.year >= currentYear);
  const currentPoint = valued.find((p) => p.kind === 'current') ?? null;

  const labelYears = new Set<number>([
    valued[0].year,
    valued[valued.length - 1].year,
    ...(currentPoint ? [currentPoint.year] : []),
  ]);

  const gridTicks = Array.from({ length: GRID_TICKS }, (_, k) => {
    const v = domainMin + (k / (GRID_TICKS - 1)) * (domainMax - domainMin);
    return { v, y: yFor(v) };
  });

  const cx = xFor(currentYear);

  const bonusPolyline = bonusPts.map((b) => `${xFor(b.year).toFixed(1)},${yFor(b.value).toFixed(1)}`).join(' ');
  const bonusProjPolyline = bonusProjPts.map((b) => `${xFor(b.year).toFixed(1)},${yFor(b.value).toFixed(1)}`).join(' ');

  const currentExtras: { name: string; value: number; color: string }[] = [];
  const bonusCur = bonusPts.find((b) => b.year === currentYear);
  const bonusProjCur = bonusProjPts.find((b) => b.year === currentYear);
  if (bonusCur) currentExtras.push({ name: t('gest:management.chart.bonus'), value: bonusCur.value, color: BONUS_COLOR });
  else if (bonusProjCur)
    currentExtras.push({ name: t('gest:management.chart.bonusProjected'), value: bonusProjCur.value, color: BONUS_COLOR });
  if (benefitPt && benefitPt.year === currentYear)
    currentExtras.push({ name: t('gest:management.chart.benefits'), value: benefitPt.annual, color: BENEFIT_COLOR });
  const EXTRA_ROW_H = 14;
  const extrasAnchorY =
    currentExtras.length > 0 ? currentExtras.reduce((sum, e) => sum + yFor(e.value), 0) / currentExtras.length : 0;
  const extrasStartY = extrasAnchorY - ((currentExtras.length - 1) * EXTRA_ROW_H) / 2;

  const mutedFill = 'fill-slate-500 dark:fill-slate-400';
  const strongFill = 'fill-slate-800 dark:fill-slate-100';
  const gridStroke = 'text-slate-200 dark:text-slate-600';

  return (
    <svg
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      width="100%"
      role="img"
      aria-label={t('gest:management.chart.ariaLabel')}
      className="overflow-visible"
    >
      {gridTicks.map((tick) => (
        <g key={`grid-${tick.y.toFixed(0)}`}>
          <line x1={x0} x2={x1} y1={tick.y} y2={tick.y} className={gridStroke} stroke="currentColor" strokeWidth={1} opacity={0.5} />
          <text x={x0 - 8} y={tick.y + 3} textAnchor="end" className={mutedFill} style={{ fontSize: 9 }}>
            {compactDollar(tick.v)}
          </text>
        </g>
      ))}

      <line x1={cx} x2={cx} y1={y0 - 10} y2={y1} className={gridStroke} stroke="currentColor" strokeWidth={1} strokeDasharray="2 3" />

      {pastPts.length >= 2 ? (
        <polyline points={polyline(pastPts)} fill="none" stroke={SALARY_COLOR} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
      ) : null}
      {futurePts.length >= 2 ? (
        <polyline points={polyline(futurePts)} fill="none" stroke={SALARY_COLOR} strokeWidth={2} strokeDasharray="5 4" strokeLinecap="round" strokeLinejoin="round" opacity={0.7} vectorEffect="non-scaling-stroke" />
      ) : null}

      {valued.map((p, i) => {
        if (i === 0) return null;
        const prev = valued[i - 1];
        const label = pctLabel(prev.annual as number, p.annual as number);
        if (!label) return null;
        const [ax, ay] = coord(prev);
        const [bx, by] = coord(p);
        return (
          <text key={`pct-${p.year}`} x={(ax + bx) / 2} y={(ay + by) / 2 + 16} textAnchor="middle" className={mutedFill} style={{ fontSize: 10 }}>
            {label}
          </text>
        );
      })}

      {bonusProjPts.length >= 2 ? (
        <polyline points={bonusProjPolyline} fill="none" stroke={BONUS_COLOR} strokeWidth={2} strokeDasharray="5 4" strokeLinecap="round" strokeLinejoin="round" opacity={0.7} vectorEffect="non-scaling-stroke" />
      ) : null}
      {bonusProjPts.map((b) => (
        <circle key={`bonus-proj-${b.year}`} cx={xFor(b.year)} cy={yFor(b.value)} r={2.5} className="fill-white dark:fill-slate-800" stroke={BONUS_COLOR} strokeWidth={1.5} opacity={0.8} vectorEffect="non-scaling-stroke" />
      ))}

      {bonusPts.length >= 2 ? (
        <polyline points={bonusPolyline} fill="none" stroke={BONUS_COLOR} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
      ) : null}
      {bonusPts.map((b) => (
        <circle key={`bonus-${b.year}`} cx={xFor(b.year)} cy={yFor(b.value)} r={b.year === currentYear ? 4 : 2.5} fill={BONUS_COLOR} vectorEffect="non-scaling-stroke" />
      ))}

      {benefitPt ? (
        <circle key="benefit" cx={xFor(benefitPt.year)} cy={yFor(benefitPt.annual)} r={4} fill={BENEFIT_COLOR} vectorEffect="non-scaling-stroke" />
      ) : null}

      {currentExtras.map((e, i) => {
        const ly = extrasStartY + i * EXTRA_ROW_H + 4;
        const py = yFor(e.value);
        return (
          <g key={`extra-${e.name}`}>
            <line x1={cx + 5} y1={py} x2={cx + 14} y2={ly - 3} stroke={e.color} strokeWidth={1} opacity={0.5} />
            <text x={cx + 16} y={ly} textAnchor="start" style={{ fontSize: 11, fill: e.color }}>
              {e.name} {compactDollar(e.value)}
            </text>
          </g>
        );
      })}

      {valued.map((p) => {
        const [px, py] = coord(p);
        const isCurrent = p.kind === 'current';
        const isMarker = isCurrent || p.isChange || p.isRoleChange || p.isHire;
        const roleAbove = p.isRoleChange || p.isHire ? p.roleLabel : null;
        return (
          <g key={p.year}>
            {roleAbove ? (
              <text x={px} y={py - 30} textAnchor="middle" className={mutedFill} style={{ fontSize: 10 }}>
                <title>{roleAbove}</title>
                {truncate(roleAbove, 28)}
              </text>
            ) : null}
            {labelYears.has(p.year) ? (
              <text x={px} y={py - 14} textAnchor="middle" className={isCurrent ? strongFill : mutedFill} style={{ fontSize: isCurrent ? 13 : 11, fontWeight: isCurrent ? 600 : 400 }}>
                {compactDollar(p.annual as number)}
              </text>
            ) : null}
            {(() => {
              const hollow = !isCurrent && p.kind === 'projection';
              return (
                <circle
                  cx={px}
                  cy={py}
                  r={isCurrent ? 4.5 : isMarker ? 3 : 2}
                  className={hollow ? 'fill-white dark:fill-slate-800' : ''}
                  style={hollow ? undefined : { fill: SALARY_COLOR }}
                  stroke={SALARY_COLOR}
                  strokeWidth={1.5}
                  opacity={hollow ? 0.8 : 1}
                  vectorEffect="non-scaling-stroke"
                />
              );
            })()}
            <text x={px} y={VIEW_H - 10} textAnchor="middle" className={isCurrent ? strongFill : mutedFill} style={{ fontSize: 11, fontWeight: isCurrent ? 600 : 400 }}>
              {p.year}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
